import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { useState, useContext } from "react";
import { Upload, Button, Card, Tabs, Select } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const { TabPane } = Tabs;
const { Option } = Select;

export default function BulkUpload() {
    const { authData, backUrl } = useContext(AuthContext);

    // Employee Upload States
    const [empFile, setEmpFile] = useState(null);
    const [empUploading, setEmpUploading] = useState(false);

    // Salary Upload States
    const [salaryFile, setSalaryFile] = useState(null);
    const [salaryUploading, setSalaryUploading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);

    // Leave Upload States
    const [leaveFile, setLeaveFile] = useState(null);
    const [leaveUploading, setLeaveUploading] = useState(false);

    // Generic upload props
    const createUploadProps = (setFileCallback) => ({
        beforeUpload: (file) => {
            const allowed = [
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.ms-excel"
            ];
            if (!allowed.includes(file.type)) {
                toast.error("Please upload a valid Excel file (.xlsx, .xls)");
                return Upload.LIST_IGNORE;
            }
            setFileCallback(file);
            return false;
        },
        maxCount: 1,
        onRemove: () => setFileCallback(null),
    });

    // Employee Bulk Upload
    const bulkUploadEmployees = async () => {
        if (!empFile) {
            toast.error("Please select an Excel file first!");
            return;
        }

        const formData = new FormData();
        formData.append("file", empFile);

        try {
            setEmpUploading(true);
            const res = await axios.post(
                `${backUrl}/api/payroll/bulk_upload_employees/`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );

            if (res.data.errors && res.data.errors.length > 0) {
                toast.error(
                    `Some items failed to upload: ${res.data.errors.join(", ")}`
                );
            } else {
                toast.success(
                    `${res.data.message} (${res.data.success_count} items uploaded successfully)`
                );
            }

            toast.success("Employees uploaded successfully!");
            setEmpFile(null);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Upload failed! Please check the file format.");
        } finally {
            setEmpUploading(false);
        }
    };

    // Salary Bulk Upload
    const bulkUploadSalary = async () => {
        if (!salaryFile) {
            toast.error("Please select an Excel file first!");
            return;
        }
        if (!selectedMonth || !selectedYear) {
            toast.error("Please select both month and year!");
            return;
        }

        const formData = new FormData();
        formData.append("file", salaryFile);
        formData.append("month", selectedMonth);
        formData.append("year", selectedYear);

        try {
            setSalaryUploading(true);
            await axios.post(
                `${backUrl}/api/payroll/upload-base-salary/`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            toast.success("Salary data uploaded successfully!");
            setSalaryFile(null);
            setSelectedMonth(null);
            setSelectedYear(null);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Upload failed! Please check the file format.");
        } finally {
            setSalaryUploading(false);
        }
    };

    // Leave Bulk Upload
    const bulkUploadLeave = async () => {
        if (!leaveFile) {
            toast.error("Please select an Excel file first!");
            return;
        }

        const formData = new FormData();
        formData.append("file", leaveFile);

        try {
            setLeaveUploading(true);
            await axios.post(
                `${backUrl}/api/hr/leave-balance/upload/`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            toast.success("Leave data uploaded successfully!");
            setLeaveFile(null);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Upload failed! Please check the file format.");
        } finally {
            setLeaveUploading(false);
        }
    };

    // Generate month and year options
    const months = [
        { value: "1", label: "January" },
        { value: "2", label: "February" },
        { value: "3", label: "March" },
        { value: "4", label: "April" },
        { value: "5", label: "May" },
        { value: "6", label: "June" },
        { value: "7", label: "July" },
        { value: "8", label: "August" },
        { value: "9", label: "September" },
        { value: "10", label: "October" },
        { value: "11", label: "November" },
        { value: "12", label: "December" },
    ];

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

    return (
        <Card style={{ maxWidth: 600, margin: "20px auto" }}>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                theme="colored"
            />
            <Tabs defaultActiveKey="1">
                <TabPane tab="Bulk Employee Upload" key="1">
                    <div style={{ padding: "20px 0" }}>
                        <Upload {...createUploadProps(setEmpFile)} fileList={empFile ? [empFile] : []}>
                            <Button icon={<UploadOutlined />}>Click to Select Excel File</Button>
                        </Upload>

                        <Button
                            type="primary"
                            style={{
                                marginTop: 20,
                                width: "100%",
                                backgroundColor: "#0e497b",
                                color: "white"
                            }}
                            loading={empUploading}
                            onClick={bulkUploadEmployees}
                        >
                            Upload Employees
                        </Button>
                    </div>
                </TabPane>

                <TabPane tab="Bulk Salary Upload" key="2">
                    <div style={{ padding: "20px 0" }}>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", marginBottom: 8 }}>Select Month:</label>
                            <Select
                                placeholder="Select Month"
                                style={{ width: "100%" }}
                                value={selectedMonth}
                                onChange={(value) => setSelectedMonth(value)}
                            >
                                {months.map((month) => (
                                    <Option key={month.value} value={month.value}>
                                        {month.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", marginBottom: 8 }}>Select Year:</label>
                            <Select
                                placeholder="Select Year"
                                style={{ width: "100%" }}
                                value={selectedYear}
                                onChange={(value) => setSelectedYear(value)}
                            >
                                {years.map((year) => (
                                    <Option key={year} value={year.toString()}>
                                        {year}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        <Upload {...createUploadProps(setSalaryFile)} fileList={salaryFile ? [salaryFile] : []}>
                            <Button icon={<UploadOutlined />}>Click to Select Excel File</Button>
                        </Upload>

                        <Button
                            type="primary"
                            style={{
                                marginTop: 20,
                                width: "100%",
                                backgroundColor: "#0e497b",
                                color: "white"
                            }}
                            loading={salaryUploading}
                            onClick={bulkUploadSalary}
                        >
                            Upload Salary Data
                        </Button>
                    </div>
                </TabPane>

                <TabPane tab="Bulk Leave Upload" key="3">
                    <div style={{ padding: "20px 0" }}>
                        <Upload {...createUploadProps(setLeaveFile)} fileList={leaveFile ? [leaveFile] : []}>
                            <Button icon={<UploadOutlined />}>Click to Select Excel File</Button>
                        </Upload>

                        <Button
                            type="primary"
                            style={{
                                marginTop: 20,
                                width: "100%",
                                backgroundColor: "#0e497b",
                                color: "white"
                            }}
                            loading={leaveUploading}
                            onClick={bulkUploadLeave}
                        >
                            Upload Leave Data
                        </Button>
                    </div>
                </TabPane>
            </Tabs>
        </Card>
    );
}